import React, { useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, Loader2, ShoppingCart, Store, Plus, Save, ExternalLink,
  X, Tag, Package, TrendingDown, ChevronDown, ChevronUp, Star,
  Check, Zap, ArrowRight
} from 'lucide-react';

const FOURNISSEUR_COLORS = {
  'Point P': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100' },
  'Leroy Merlin': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', badge: 'bg-green-100' },
  'Castorama': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100' },
  'Plateforme du Bâtiment': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100' },
  'Cedeo': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', badge: 'bg-cyan-100' },
  'Rexel': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', badge: 'bg-indigo-100' },
  'Bricoman': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-100' },
};

const DEFAULT_COLOR = { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', badge: 'bg-slate-100' };

export default function PriceSearchPanel({ onSelectMaterial, compact = false }) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [showPanel, setShowPanel] = useState(!compact);
  const inputRef = useRef(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    try {
      const res = await api.post('/fournisseurs/search-prix', { query: query.trim() });
      setResults(res.data);
    } catch (e) {
      console.error(e);
    }
    setSearching(false);
  };

  const handleSaveToCatalog = async (item) => {
    setSavingId(item.nom);
    try {
      await api.post('/fournisseurs/catalogue', {
        nom: item.nom,
        reference: item.reference || '',
        fournisseur: item.fournisseur,
        prix_unitaire: item.prix_unitaire,
        unite: item.unite,
        categorie: '',
        description: item.description || item.conditionnement || '',
        lien_url: item.url || ''
      });
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const handleSelect = (item) => {
    if (onSelectMaterial) {
      onSelectMaterial({
        description: item.nom,
        prix_unitaire: item.prix_unitaire,
        unite: item.unite,
        fournisseur: item.fournisseur,
        reference: item.reference
      });
    }
  };

  const allResults = [
    ...(results?.internal_results || []).map(r => ({ ...r, isInternal: true })),
    ...(results?.web_results || [])
  ];

  const meta = results?.web_results?.find(r => r._meta)?._meta || {};
  const cheapest = allResults.length > 0 
    ? allResults.reduce((min, r) => r.prix_unitaire < min.prix_unitaire ? r : min, allResults[0])
    : null;

  // Compact toggle button
  if (compact && !showPanel) {
    return (
      <button
        onClick={() => { setShowPanel(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        className="w-full p-3 border-2 border-dashed border-violet-300 rounded-xl text-violet-600 hover:bg-violet-50 transition-colors flex items-center justify-center gap-2 font-medium"
        data-testid="open-price-search"
      >
        <Search className="w-4 h-4" />
        Rechercher prix fournisseurs
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="price-search-panel">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-violet-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Store className="w-4 h-4 text-violet-600" />
            Recherche prix fournisseurs
          </h3>
          {compact && (
            <button onClick={() => setShowPanel(false)} className="p-1 hover:bg-white/50 rounded">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Ex: Plaque BA13, Volet roulant PVC 120x140, Peinture acrylique..."
            className="flex-1 bg-white"
            data-testid="price-search-input"
          />
          <Button
            onClick={handleSearch}
            disabled={searching || !query.trim()}
            className="bg-violet-600 hover:bg-violet-700 px-6"
            data-testid="price-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Loading */}
      {searching && (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Recherche des prix chez les fournisseurs BTP...</p>
        </div>
      )}

      {/* Results */}
      {results && !searching && (
        <div className="divide-y divide-slate-100">
          {/* Summary bar */}
          {allResults.length > 0 && (
            <div className="p-3 bg-slate-50 flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="text-slate-500">{allResults.length} résultats</span>
                {meta.fourchette_prix && (
                  <span className="text-slate-700 font-medium">Fourchette : {meta.fourchette_prix}</span>
                )}
              </div>
              {meta.conseil && (
                <span className="text-violet-600 text-xs max-w-md truncate">{meta.conseil}</span>
              )}
            </div>
          )}

          {/* Result items */}
          {allResults.map((item, idx) => {
            const colors = FOURNISSEUR_COLORS[item.fournisseur] || DEFAULT_COLOR;
            const isCheapest = cheapest && item.prix_unitaire === cheapest.prix_unitaire && item.fournisseur === cheapest.fournisseur;

            return (
              <div key={idx} className={`p-4 hover:bg-slate-50 transition-colors ${isCheapest ? 'bg-green-50/30' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  {item.image_url && (
                    <a
                      href={item.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center"
                      title="Voir le produit"
                    >
                      <img
                        src={item.image_url}
                        alt={item.nom}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors.badge} ${colors.text}`}>
                        {item.fournisseur}
                      </span>
                      {item.isInternal && (
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                          Catalogue interne
                        </span>
                      )}
                      {isCheapest && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" /> Meilleur prix
                        </span>
                      )}
                      {item.disponibilite && item.disponibilite !== 'Inconnu' && (
                        <span className={`text-xs ${item.disponibilite === 'En stock' ? 'text-green-600' : 'text-amber-600'}`}>
                          {item.disponibilite}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{item.nom}</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {item.reference && (
                        <p className="text-xs text-slate-500">
                          <span className="font-semibold text-slate-600">Réf:</span> <span className="font-mono">{item.reference}</span>
                        </p>
                      )}
                      {item.conditionnement && <p className="text-xs text-slate-400">{item.conditionnement}</p>}
                      {item.description && <p className="text-xs text-slate-500">{item.description}</p>}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5"
                        >
                          Voir la fiche produit ↗
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{item.prix_unitaire?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</p>
                      <p className="text-[10px] text-slate-400">/{item.unite} <span className="font-semibold text-emerald-600">TTC</span></p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {onSelectMaterial && (
                        <Button
                          size="sm"
                          onClick={() => handleSelect(item)}
                          className="bg-green-600 hover:bg-green-700 h-8 px-3"
                          data-testid={`select-material-${idx}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Utiliser
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSaveToCatalog(item)}
                        disabled={savingId === item.nom}
                        className="h-7 px-2 text-xs"
                      >
                        {savingId === item.nom ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3 mr-1" />}
                        {savingId === item.nom ? 'Sauvé' : 'Catalogue'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {allResults.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun résultat pour "{results.query}"</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!results && !searching && (
        <div className="p-6 text-center text-slate-400">
          <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Tapez un matériau pour comparer les prix</p>
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {['Plaque BA13', 'Volet roulant PVC', 'Peinture acrylique 10L', 'Tube PER 16', 'Câble R2V 3G2.5'].map(ex => (
              <button
                key={ex}
                onClick={() => { setQuery(ex); }}
                className="px-3 py-1 bg-slate-100 hover:bg-violet-100 text-slate-600 hover:text-violet-700 text-xs rounded-full transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
